import { useEffect, useRef, useCallback, useState } from 'react';
import { BACKEND_URL } from '@/config/api';

export const useWebSocket = (boardId, token, user, enabled = true) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [users, setUsers] = useState([]);
  const [remoteCursors, setRemoteCursors] = useState({});
  const [remoteSelections, setRemoteSelections] = useState({});
  const shouldReconnectRef = useRef(true);
  
  // Event handlers refs to avoid stale closures
  const handlersRef = useRef({
    onObjectCreated: null,
    onObjectUpdated: null,
    onObjectDeleted: null,
    onObjectsBatchUpdate: null,
    onCommentCreated: null,
    onCommentUpdated: null,
    onCommentDeleted: null,
    onCommentReplyAdded: null,
    onCommentReaction: null,
    onNewMessage: null,
  });

  // Set event handlers
  const setHandlers = useCallback((handlers) => {
    handlersRef.current = { ...handlersRef.current, ...handlers };
  }, []);

  // Send cursor position
  const sendCursorMove = useCallback((x, y) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'cursor_move',
        x,
        y
      }));
    }
  }, []);

  // Send selection changed event
  const sendSelectionChanged = useCallback((selectedIds) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'selection_changed',
        selected_ids: selectedIds
      }));
    }
  }, []);

  // Send object created event
  const sendObjectCreated = useCallback((object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'object_created',
        object
      }));
    }
  }, []);

  // Send object updated event
  const sendObjectUpdated = useCallback((objectId, changes) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'object_updated',
        object_id: objectId,
        changes
      }));
    }
  }, []);

  // Send object deleted event
  const sendObjectDeleted = useCallback((objectIds) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'object_deleted',
        object_ids: objectIds
      }));
    }
  }, []);

  // Send batch update event (for alignment, etc.)
  const sendObjectsBatchUpdate = useCallback((objects) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'objects_batch_update',
        objects
      }));
    }
  }, []);

  // Connect on mount and when dependencies change
  useEffect(() => {
    if (!boardId || !enabled) return;

    // Calculate WebSocket URL using runtime config
    if (!BACKEND_URL) {
      console.error('[WS] BACKEND_URL is not set');
      return;
    }

    const wsBaseUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    shouldReconnectRef.current = true;

    const createConnection = () => {
      // Close existing connection
      if (wsRef.current) {
        wsRef.current.close();
      }

      const wsUrl = `${wsBaseUrl}/api/ws/whiteboard/${boardId}${token ? `?token=${token}` : ''}`;
      console.log('[WS] Connecting to:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS] Connected');
        setIsConnected(true);
        
        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000); // Ping every 30 seconds
      };

      ws.onclose = (event) => {
        console.log('[WS] Disconnected:', event.code, event.reason);
        setIsConnected(false);
        setUsers([]);
        setRemoteCursors({});
        setRemoteSelections({});
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        
        // Attempt to reconnect after 3 seconds if still enabled
        if (shouldReconnectRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('[WS] Attempting to reconnect...');
            createConnection();
          }, 3000);
        }
      };

      ws.onerror = (error) => {
        console.error('[WS] Error:', error);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'room_state':
              // Initial state when joining the room
              setUsers(data.users || []);
              setRemoteCursors(data.cursors || {});
              // Set initial selections from room state
              if (data.selections) {
                setRemoteSelections(data.selections);
              }
              console.log('[WS] Room state received:', data.users?.length || 0, 'users');
              break;
              
            case 'user_joined':
              console.log('[WS] User joined:', data.user?.name);
              setUsers(data.users || []);
              break;
              
            case 'user_left':
              console.log('[WS] User left:', data.user?.name);
              setUsers(data.users || []);
              // Remove cursor and selection of the user who left
              setRemoteCursors(prev => {
                const newCursors = { ...prev };
                if (data.user?.id) {
                  delete newCursors[data.user.id];
                }
                return newCursors;
              });
              setRemoteSelections(prev => {
                const newSelections = { ...prev };
                if (data.user?.id) {
                  delete newSelections[data.user.id];
                }
                return newSelections;
              });
              break;
              
            case 'cursor_update':
              // Update remote cursor position
              setRemoteCursors(prev => ({
                ...prev,
                [data.user_id]: data.cursor
              }));
              break;
              
            case 'selection_changed':
              // Update remote user selection for presence indicators
              setRemoteSelections(prev => {
                const newSelections = { ...prev };
                if (data.selected_ids && data.selected_ids.length > 0) {
                  newSelections[data.user_id] = {
                    user: data.user,
                    selected_ids: data.selected_ids
                  };
                } else {
                  // User deselected everything
                  delete newSelections[data.user_id];
                }
                return newSelections;
              });
              break;
              
            case 'object_created':
              if (handlersRef.current.onObjectCreated) {
                handlersRef.current.onObjectCreated(data.object, data.user_id);
              }
              break;
              
            case 'object_updated':
              if (handlersRef.current.onObjectUpdated) {
                handlersRef.current.onObjectUpdated(data.object_id, data.changes, data.user_id);
              }
              break;
              
            case 'object_deleted':
              if (handlersRef.current.onObjectDeleted) {
                handlersRef.current.onObjectDeleted(data.object_ids, data.user_id);
              }
              // Also clear selections for deleted objects
              setRemoteSelections(prev => {
                const newSelections = { ...prev };
                const deletedIds = data.object_ids || [];
                for (const userId in newSelections) {
                  newSelections[userId].selected_ids = newSelections[userId].selected_ids.filter(
                    id => !deletedIds.includes(id)
                  );
                  if (newSelections[userId].selected_ids.length === 0) {
                    delete newSelections[userId];
                  }
                }
                return newSelections;
              });
              break;
              
            case 'objects_batch_update':
              if (handlersRef.current.onObjectsBatchUpdate) {
                handlersRef.current.onObjectsBatchUpdate(data.objects, data.user_id);
              }
              break;
              
            case 'comment_created':
              if (handlersRef.current.onCommentCreated) {
                handlersRef.current.onCommentCreated(data.comment, data.user_id);
              }
              break;
              
            case 'comment_updated':
              if (handlersRef.current.onCommentUpdated) {
                handlersRef.current.onCommentUpdated(data.comment, data.user_id);
              }
              break;
              
            case 'comment_deleted':
              if (handlersRef.current.onCommentDeleted) {
                handlersRef.current.onCommentDeleted(data.comment_id, data.user_id);
              }
              break;
              
            case 'comment_reply_added':
              if (handlersRef.current.onCommentReplyAdded) {
                handlersRef.current.onCommentReplyAdded(data.comment, data.user_id);
              }
              break;
              
            case 'comment_reaction':
              if (handlersRef.current.onCommentReaction) {
                handlersRef.current.onCommentReaction(data.comment, data.user_id);
              }
              break;
              
            case 'new_message':
              if (handlersRef.current.onNewMessage) {
                handlersRef.current.onNewMessage(data.message, data.user_id);
              }
              break;
              
            case 'pong':
              // Keep-alive response
              break;
              
            default:
              console.log('[WS] Unknown message type:', data.type);
          }
        } catch (error) {
          console.error('[WS] Error parsing message:', error);
        }
      };
    };

    createConnection();
    
    return () => {
      // Cleanup on unmount
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [boardId, token, enabled]);

  return {
    isConnected,
    users,
    remoteCursors,
    remoteSelections,
    setHandlers,
    sendCursorMove,
    sendSelectionChanged,
    sendObjectCreated,
    sendObjectUpdated,
    sendObjectDeleted,
    sendObjectsBatchUpdate,
  };
};
